import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import type { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import { LojaDataGrid } from "../../../common";
import { podeEditarTituloPagar } from "../hooks/useEditarTituloPagar";
import type {
  ContaPagarStatusTipo,
  ContaPagarView,
} from "../types/contas-pagar.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";

type Row = ContaPagarView;

function chipColor(
  tipo: ContaPagarStatusTipo
): "success" | "error" | "warning" | "default" {
  switch (tipo) {
    case "pago":
      return "success";
    case "vencido":
      return "error";
    case "pendente":
      return "warning";
    default:
      return "default";
  }
}

type Props = {
  linhas: ContaPagarView[];
  total: number;
  page: number;
  limit: number;
  loading?: boolean;
  onPaginationModelChange: (m: GridPaginationModel) => void;
  onBaixar?: (row: ContaPagarView) => void;
  onCancelar?: (row: ContaPagarView) => void;
  onEditar?: (row: ContaPagarView) => void;
  /** Visão resumida: evento (sem categoria) ou operacional (com categoria). */
  variant?: "completa" | "evento" | "operacional";
  /** Sem Card externo (ex.: embutido em outro bloco da página). */
  embedded?: boolean;
};

function podeExibirBaixaPagar(row: ContaPagarView): boolean {
  if (row.valorAberto <= 0) return false;
  if (row.statusTipo === "pago" || row.statusTipo === "cancelado") return false;
  return true;
}

function podeExibirCancelarPagar(row: ContaPagarView): boolean {
  if (row.statusTipo === "neutro") return false;
  if (row.statusTipo === "pago" || row.statusTipo === "cancelado") return false;
  if (row.valorAberto <= 0) return false;
  return row.statusTipo === "pendente" || row.statusTipo === "vencido";
}

export default function ContasPagarTabela({
  linhas,
  total,
  page,
  limit,
  loading,
  onPaginationModelChange,
  onBaixar,
  onCancelar,
  onEditar,
  variant = "completa",
  embedded = false,
}: Props) {
  const theme = useTheme();
  const modoEvento = variant === "evento";
  const modoCompacto = variant !== "completa";

  const rows: Row[] = linhas.map((r) => ({ ...r, id: r.id }));

  const colData: GridColDef<Row> = {
    field: "dataVencimentoLabel",
    headerName: modoEvento ? "Data" : "Vencimento",
    width: modoCompacto ? 108 : 118,
    renderCell: (p) => {
      const dest = p.row.vencido && p.row.statusTipo !== "pago";
      return (
        <Typography
          variant="body2"
          sx={{
            py: 0.5,
            fontWeight: dest ? 700 : 400,
            color: dest ? theme.palette.error.main : undefined,
          }}
        >
          {p.row.dataVencimentoLabel}
          {!modoEvento && p.row.diasAtraso > 0 && p.row.vencido ? (
            <Typography
              component="span"
              variant="caption"
              display="block"
              color="error"
            >
              {p.row.diasAtraso}d atraso
            </Typography>
          ) : null}
        </Typography>
      );
    },
  };

  const colDescricao: GridColDef<Row> = {
    field: "descricao",
    headerName: "Descrição",
    flex: 1,
    minWidth: modoCompacto ? 160 : 140,
    renderCell: (p) => (
      <Typography
        variant="body2"
        sx={{ py: 0.5 }}
        noWrap
        title={p.row.descricao}
      >
        {p.row.descricao}
      </Typography>
    ),
  };

  const colCategoria: GridColDef<Row> = {
    field: "categoriaLabel",
    headerName: "Categoria",
    width: 120,
    renderCell: (p) => (
      <Typography
        variant="body2"
        sx={{ py: 0.5 }}
        noWrap
        title={p.row.categoriaLabel}
      >
        {p.row.categoriaLabel}
      </Typography>
    ),
  };

  const colValorTotal: GridColDef<Row> = {
    field: "valorTotal",
    headerName: "Valor total",
    width: 118,
    align: "right",
    headerAlign: "right",
    renderCell: (p) => (
      <Typography
        variant="body2"
        sx={{ py: 0.5 }}
        fontWeight={modoCompacto ? 600 : 400}
      >
        {formatarMoedaBRL(p.row.valorTotal)}
      </Typography>
    ),
  };

  const colStatus: GridColDef<Row> = {
    field: "statusLabel",
    headerName: "Status",
    width: 118,
    renderCell: (p) => (
      <Chip
        size="small"
        label={p.row.statusLabel}
        color={chipColor(p.row.statusTipo)}
        variant={p.row.statusTipo === "pendente" ? "filled" : "outlined"}
        sx={{ my: 0.5 }}
      />
    ),
  };

  const colAcoes: GridColDef<Row> | null =
    onBaixar || onCancelar || onEditar
      ? ({
          field: "acoes",
          headerName: "Ações",
          sortable: false,
          filterable: false,
          width: modoCompacto
            ? [onBaixar, onCancelar, onEditar].filter(Boolean).length >= 3
              ? 300
              : [onBaixar, onCancelar, onEditar].filter(Boolean).length === 2
                ? 220
                : 120
            : [onBaixar, onCancelar, onEditar].filter(Boolean).length >= 3
              ? 320
              : [onBaixar, onCancelar, onEditar].filter(Boolean).length === 2
                ? 228
                : 124,
          renderCell: (p: { row: Row }) => (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              sx={{ py: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {onEditar ? (
                <MDButton
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ py: 0.25, px: 1, minHeight: 30 }}
                  disabled={!podeEditarTituloPagar(p.row) || Boolean(loading)}
                  onClick={() => onEditar(p.row)}
                >
                  Editar
                </MDButton>
              ) : null}
              {onBaixar ? (
                <MDButton
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ py: 0.25, px: 1, minHeight: 30 }}
                  disabled={!podeExibirBaixaPagar(p.row) || Boolean(loading)}
                  onClick={() => onBaixar(p.row)}
                >
                  Marcar pago
                </MDButton>
              ) : null}
              {onCancelar ? (
                <MDButton
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ py: 0.25, px: 1, minHeight: 30 }}
                  disabled={!podeExibirCancelarPagar(p.row) || Boolean(loading)}
                  onClick={() => onCancelar(p.row)}
                >
                  Cancelar
                </MDButton>
              ) : null}
            </Stack>
          ),
        } as GridColDef<Row>)
      : null;

  const columns: GridColDef<Row>[] =
    variant === "evento"
      ? [
          colData,
          colDescricao,
          colValorTotal,
          colStatus,
          ...(colAcoes ? [colAcoes] : []),
        ]
      : variant === "operacional"
        ? [
            colData,
            colDescricao,
            colCategoria,
            colValorTotal,
            colStatus,
            ...(colAcoes ? [colAcoes] : []),
          ]
        : [
            colData,
            {
              field: "fornecedorNome",
              headerName: "Fornecedor",
              flex: 1,
              minWidth: 150,
              renderCell: (p) => (
                <Box sx={{ py: 0.5, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    title={p.row.fornecedorNome}
                  >
                    {p.row.fornecedorNome}
                  </Typography>
                  {p.row.fornecedorDocumento ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                    >
                      {p.row.fornecedorDocumento}
                    </Typography>
                  ) : null}
                </Box>
              ),
            },
            colDescricao,
            {
              field: "naturezaLabel",
              headerName: "Natureza",
              width: 100,
              renderCell: (p) => (
                <Typography variant="body2" sx={{ py: 0.5 }}>
                  {p.row.naturezaLabel}
                </Typography>
              ),
            },
            colCategoria,
            {
              field: "contextoLabel",
              headerName: "Contexto",
              width: 120,
              renderCell: (p) => (
                <Typography variant="body2" sx={{ py: 0.5 }}>
                  {p.row.contextoLabel}
                </Typography>
              ),
            },
            {
              field: "centroCustoNome",
              headerName: "Centro de custo",
              flex: 0.85,
              minWidth: 120,
              renderCell: (p) => (
                <Typography
                  variant="body2"
                  sx={{
                    py: 0.5,
                    fontWeight: p.row.semCentro ? 600 : 400,
                    color: p.row.semCentro
                      ? theme.palette.warning.dark
                      : undefined,
                  }}
                >
                  {p.row.centroCustoNome}
                </Typography>
              ),
            },
            colValorTotal,
            {
              field: "valorPago",
              headerName: "Valor pago",
              width: 110,
              align: "right",
              headerAlign: "right",
              renderCell: (p) => (
                <Typography variant="body2" sx={{ py: 0.5 }}>
                  {formatarMoedaBRL(p.row.valorPago)}
                </Typography>
              ),
            },
            {
              field: "valorAberto",
              headerName: "Valor aberto",
              width: 118,
              align: "right",
              headerAlign: "right",
              renderCell: (p) => (
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    py: 0.5,
                    color:
                      p.row.valorAberto > 0 && p.row.vencido
                        ? theme.palette.error.main
                        : undefined,
                  }}
                >
                  {formatarMoedaBRL(p.row.valorAberto)}
                </Typography>
              ),
            },
            colStatus,
            {
              field: "pagoEmLabel",
              headerName: "Pago em",
              width: 102,
              renderCell: (p) => (
                <Typography variant="body2" sx={{ py: 0.5 }}>
                  {p.row.pagoEmLabel}
                </Typography>
              ),
            },
            ...(colAcoes ? [colAcoes] : []),
          ];

  const handleRowClick = modoCompacto
    ? (params: { row: Row }) => {
        if (!onEditar || loading || !podeEditarTituloPagar(params.row)) return;
        onEditar(params.row);
      }
    : undefined;

  const minWidthGrid =
    variant === "evento"
      ? 720
      : variant === "operacional"
        ? 860
        : onBaixar && onCancelar && onEditar
          ? 1460
          : onBaixar && onCancelar
            ? 1368
            : onBaixar || onCancelar
              ? 1288
              : 1120;

  const grid = (
    <LojaDataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      paginationMode="server"
      rowCount={total}
      paginationModel={{
        page: Math.max(0, page - 1),
        pageSize: limit,
      }}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={handleRowClick}
      pageSizeOptions={[10, 20, 50, 100]}
      disableColumnMenu
      columnHeaderHeight={44}
      rowHeight={52}
      getRowClassName={(params) => {
        const classes: string[] = [];
        if (params.row.vencido && params.row.statusTipo !== "pago") {
          classes.push("cp-row-vencido");
        }
        if (modoCompacto && onEditar && podeEditarTituloPagar(params.row)) {
          classes.push("cp-row-clicavel");
        }
        return classes.join(" ");
      }}
      sx={{
        minWidth: minWidthGrid,
        "& .cp-row-vencido": {
          backgroundColor: alpha(theme.palette.error.main, 0.06),
        },
        "& .cp-row-clicavel": {
          cursor: "pointer",
        },
        ...(modoEvento
          ? {
              "& .MuiDataGrid-row:hover": {
                bgcolor: "rgba(249, 96, 48, 0.04)",
              },
            }
          : {}),
      }}
    />
  );

  if (embedded) {
    return (
      <Box sx={{ width: "100%", overflowX: modoCompacto ? "hidden" : "auto" }}>
        {grid}
      </Box>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
          >
            Títulos a pagar
          </Typography>
        </Box>
        <Box
          sx={{
            px: 2,
            pb: 2,
            width: "100%",
            overflowX: modoCompacto ? "hidden" : "auto",
          }}
        >
          {grid}
        </Box>
      </CardContent>
    </Card>
  );
}
